import api from '@/api';
import { IResident } from '@/types/api/traversal/getNursingHomeResidents';
import { IMdResident } from '@/types/api/medical/getResidents';
import { IGetResidentRes } from '@/types/api/resident/getResident';
import { ResidentSortData } from '@/utils/residentSort';

type CurrentResident = IResident | IMdResident;

// 表示対象の日付にかかわらず、通知・ケアの並び替えには現在情報を使う。
export const fetchCurrentResidents = async (
  nursingHomeId: string, permittedUnitId: number | null, medical: boolean,
): Promise<CurrentResident[]> => {
  const config = { params: { nursingHomeId, ...(permittedUnitId !== null ? { nursingHomeUnitId: permittedUnitId } : {}) } };
  try {
    const result = medical
      ? await api.get('/traversal/md/nursing-homes/:nursingHomeId/residents', config)
      : await api.get('/traversal/nursing-homes/:nursingHomeId', config);
    return result.residents;
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status === 404) return [];
    throw error;
  }
};

export const fetchResidentSortMetadata = async (
  nursingHomeId: string, permittedUnitId: number | null,
): Promise<IGetResidentRes[]> => {
  const residents: IGetResidentRes[] = [];
  const limit = 10000;
  for (let offset = 0; ; offset += limit) {
    const page = await api.get('/nursing-homes/:nursingHomeId/residents', {
      params: {
        nursingHomeId, limit, offset,
        // eslint-disable-next-line camelcase -- APIが要求するクエリパラメータ名を維持する。
        ...(permittedUnitId !== null ? { nursing_home_unit_id: permittedUnitId } : {}),
      },
    });
    residents.push(...page);
    if (page.length < limit) return residents;
  }
};

export const createResidentSortData = (
  current: CurrentResident[], metadata: IGetResidentRes[],
): Map<number, ResidentSortData> => {
  const byId = new Map(metadata.map((resident) => [resident.id, resident]));
  return new Map(current.map((resident) => {
    const details = byId.get(resident.id);
    // 取得漏れを「ベッド未設定」等として並べ替えない。
    if (!details) throw new Error('Resident sort metadata is incomplete');
    return [resident.id, {
      id: resident.id,
      name: (details.lastName ?? '') + (details.name ?? ''),
      nursingHomeUnitName: details.nursingHomeUnitName ?? '',
      roomName: details.roomName ?? '',
      bed: details.bed ?? null,
      gasDaysOfNoDefecation: details.gasDaysOfNoDefecation ?? null,
      latestUrineVolumeCalculationTimestamp: details.latestUrineVolumeCalculationTimestamp ?? null,
      coverTimestamp: resident.coverTimestamp,
      nextCareTiming: resident.nextCareTiming,
      inCareTiming: resident.inCareTiming,
      isCareCompleted: resident.isCareCompleted,
    }];
  }));
};
